export const PRIMARY_DESTINATIONS = Object.freeze([
  {
    key: 'home',
    label: 'Home',
    canonicalPath: '/home',
    purpose: 'Resume reading and see the next useful action.'
  },
  {
    key: 'library',
    label: 'Library',
    canonicalPath: '/library',
    purpose: 'Find, organize, add, and open books.'
  },
  {
    key: 'reading',
    label: 'Reading',
    canonicalPath: '/reading',
    purpose: 'Return to the active book or choose a book to read.'
  },
  {
    key: 'notes',
    label: 'Notes',
    canonicalPath: '/notes',
    purpose: 'Capture and revisit source-linked knowledge.'
  },
  {
    key: 'progress',
    label: 'Progress',
    canonicalPath: '/progress',
    purpose: 'Review activity, goals, achievements, and motivation settings.'
  }
]);

// Phase 0 contract only. App.jsx remains authoritative until shell_v2 ships.
// Every current route and known in-product deep link must appear here.
export const ROUTE_COMPATIBILITY = Object.freeze([
  { current: '/', future: '/', destination: 'public', behavior: 'preserve', access: 'public' },
  { current: '/signup', future: '/signup', destination: 'public', behavior: 'preserve', access: 'public' },
  { current: '/register', future: '/signup', destination: 'public', behavior: 'redirect', access: 'public' },
  { current: '/login', future: '/login', destination: 'public', behavior: 'preserve', access: 'public' },
  { current: '/forgot-password', future: '/forgot-password', destination: 'public', behavior: 'preserve', access: 'public' },
  { current: '/reset-password', future: '/reset-password', destination: 'public', behavior: 'preserve', access: 'public' },
  { current: '/update-password', future: '/reset-password', destination: 'public', behavior: 'redirect', access: 'public' },
  { current: '/dashboard', future: '/home', destination: 'home', behavior: 'alias', access: 'protected' },
  { current: '/library', future: '/library', destination: 'library', behavior: 'preserve', access: 'protected' },
  { current: '/upload', future: '/library?panel=upload', destination: 'library', behavior: 'alias', access: 'protected' },
  { current: '/collections', future: '/library?view=collections', destination: 'library', behavior: 'alias', access: 'protected' },
  { current: '/recommendations', future: '/library?view=recommended', destination: 'library', behavior: 'alias', access: 'protected' },
  { current: '/read/:bookId', future: '/reading/:bookId', destination: 'reading', behavior: 'alias-preserve-query', access: 'protected' },
  { current: '/notes', future: '/notes', destination: 'notes', behavior: 'preserve', access: 'protected' },
  { current: '/clippings', future: '/notes?filter=clippings', destination: 'notes', behavior: 'alias', access: 'protected' },
  { current: '/mentor', future: '/notes?tool=mentor', destination: 'notes', behavior: 'alias', access: 'protected' },
  { current: '/progress', future: '/progress', destination: 'progress', behavior: 'preserve', access: 'protected' },
  { current: '/achievements', future: '/progress?view=achievements', destination: 'progress', behavior: 'alias', access: 'protected' },
  { current: '/goals', future: '/progress?view=goals', destination: 'progress', behavior: 'alias', access: 'protected' },
  { current: '/streak', future: '/progress?view=overview', destination: 'progress', behavior: 'alias', access: 'protected' },
  { current: '/gamification', future: '/progress?view=achievements', destination: 'progress', behavior: 'alias', access: 'protected' },
  { current: '/leaderboard', future: '/progress?view=achievements&detail=leaderboard', destination: 'progress', behavior: 'alias', access: 'protected' },
  { current: '/settings', future: '/settings', destination: 'utility', behavior: 'preserve', access: 'protected' },
  { current: '/settings/data-export', future: '/settings/data-export', destination: 'utility', behavior: 'preserve', access: 'protected' },
  { current: '/profile', future: '/settings?section=profile', destination: 'utility', behavior: 'repair-missing-route', access: 'protected' },
  { current: '/onboarding', future: '/onboarding', destination: 'utility', behavior: 'preserve', access: 'protected' },
  { current: '/help', future: '/help', destination: 'utility', behavior: 'preserve', access: 'protected' },
  { current: '/help/viewer', future: '/help/viewer', destination: 'utility', behavior: 'preserve', access: 'protected' },
  { current: '/feedback', future: '/feedback', destination: 'utility', behavior: 'preserve', access: 'protected' },
  { current: '/contact', future: '/contact', destination: 'public', behavior: 'preserve', access: 'public' },
  { current: '/jolman-press', future: '/jolman-press', destination: 'public', behavior: 'preserve', access: 'public' },
  { current: '/legal/privacy-policy', future: '/legal/privacy-policy', destination: 'public', behavior: 'preserve', access: 'public' },
  { current: '/legal/terms-of-service', future: '/legal/terms-of-service', destination: 'public', behavior: 'preserve', access: 'public' },
  { current: '/legal/cookie-policy', future: '/legal/cookie-policy', destination: 'public', behavior: 'preserve', access: 'public' },
  { current: '/pricing', future: '/pricing', destination: 'public', behavior: 'preserve', access: 'public' },
  { current: '/premium', future: '/pricing', destination: 'public', behavior: 'redirect', access: 'public' },
  { current: '*', future: '/home', destination: 'compatibility', behavior: 'authenticated-home-fallback', access: 'mixed' }
]);

const routePatternToRegex = (pattern) => {
  if (pattern === '*') return /^.*$/;
  const expression = pattern
    .split('/')
    .map((segment) => (segment.startsWith(':') ? '[^/]+' : segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')))
    .join('/');
  return new RegExp(`^${expression}/?$`);
};

export const getRouteCompatibility = (pathname) => {
  const path = typeof pathname === 'string' ? pathname.split(/[?#]/)[0] || '/' : '/';
  return ROUTE_COMPATIBILITY.find(({ current }) => current !== '*' && routePatternToRegex(current).test(path))
    || ROUTE_COMPATIBILITY.find(({ current }) => current === '*');
};

export const getDestination = (key) => PRIMARY_DESTINATIONS.find((destination) => destination.key === key);
